import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Search, Eye } from 'lucide-react';

const CATEGORIES = ['all','notes','gaming','clothing','electronics','books','sports','food','other'];
const STATUSES   = ['all','active','removed_by_admin','removed_by_seller'];

export default function AdminProducts() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [category, setCategory] = useState('all');
  const [status,   setStatus]   = useState('all');
  const [page,     setPage]     = useState(1);
  const [pages,    setPages]    = useState(1);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page });
      if (search.trim()) params.set('search', search.trim());
      if (category !== 'all') params.set('category', category);
      if (status   !== 'all') params.set('status', status);
      const { data } = await api.get(`/admin/products?${params}`);
      setProducts(data.products);
      setPages(data.pages);
    } catch { toast.error('Could not load products.'); }
    finally { setLoading(false); }
  }, [search, category, status, page]);

  useEffect(() => {
    const t = setTimeout(fetch, 300);
    return () => clearTimeout(t);
  }, [fetch]);

  return (
    <div className="fade-in">
      <div className="admin-page-header">
        <h2>Products</h2>
      </div>

      <div className="admin-controls">
        <div className="admin-search-wrap">
          <Search size={15} className="admin-search-icon" />
          <input className="admin-search" placeholder="Search products..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>

        <select className="select" style={{ width: 140 }}
          value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}>
          {CATEGORIES.map(c => <option key={c} value={c}>{c === 'all' ? 'All Categories' : c}</option>)}
        </select>

        <select className="select" style={{ width: 160 }}
          value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          {STATUSES.map(s => <option key={s} value={s}>{s === 'all' ? 'All Status' : s.replace(/_/g,' ')}</option>)}
        </select>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Product</th><th>Seller</th><th>Price</th><th>Category</th><th>Status</th><th>Contacts</th><th></th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32 }}>
                <div className="spinner" style={{ margin: '0 auto' }} />
              </td></tr>
            ) : products.map(p => (
              <tr key={p._id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {p.images?.[0]
                      ? <img src={p.images[0]} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                      : <div style={{ width: 40, height: 40, background: 'var(--bg-2)', borderRadius: 8 }} />
                    }
                    <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{p.title}</span>
                  </div>
                </td>
                <td style={{ fontSize: '0.82rem' }}>
                  {p.seller?.name}<br />
                  <span style={{ color: 'var(--text-muted)' }}>{p.seller?.rollNumber}</span>
                </td>
                <td style={{ fontWeight: 600, color: 'var(--accent)' }}>₹{p.price.toLocaleString()}</td>
                <td><span className={`badge badge-${p.category}`}>{p.category}</span></td>
                <td>
                  <span className={`badge ${p.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                    {p.status === 'active' ? 'Active' : p.status === 'removed_by_admin' ? 'By Admin' : 'By Seller'}
                  </span>
                </td>
                <td>{p.contactCount}</td>
                <td>
                  <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/admin/products/${p._id}`)}>
                    <Eye size={14} /> View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 24 }}>
          <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span style={{ lineHeight: '36px', fontSize: '0.875rem', color: 'var(--text-muted)' }}>{page} / {pages}</span>
          <button className="btn btn-secondary btn-sm" disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}